/**
 * InstallerProfileEditDialog
 * ──────────────────────────
 * General-purpose "edit this installer's profile" form. Fills a real gap —
 * before this, the only existing contractor-edit UI was pay-profile-specific
 * (PayProfileForm). Persists via ContractorService.updateContractor, the same
 * writer other contractor edit flows already use (ContractorProfile.tsx,
 * ComplianceReviewPage.tsx, UnifiedReviewerPanel.tsx).
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ContractorService } from "@/services/contractor.service";
import type { Contractor } from "@/data/types";

interface InstallerProfileEditDialogProps {
  contractor: Contractor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (updated: Contractor) => void;
}

interface FormState {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  tradeType: string;
  licenseNumber: string;
  notes: string;
  active: boolean;
}

function toFormState(contractor: Contractor): FormState {
  return {
    companyName: contractor.companyName ?? "",
    contactName: contractor.contactName ?? "",
    email: contractor.email ?? "",
    phone: contractor.phone ?? "",
    tradeType: contractor.tradeType ?? "",
    licenseNumber: contractor.licenseNumber ?? "",
    notes: contractor.notes ?? "",
    active: contractor.active,
  };
}

export function InstallerProfileEditDialog({
  contractor,
  open,
  onOpenChange,
  onSaved,
}: InstallerProfileEditDialogProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contractor && open) setForm(toFormState(contractor));
  }, [contractor, open]);

  if (!contractor || !form) return null;

  const patch = (updates: Partial<FormState>) => setForm((prev) => (prev ? { ...prev, ...updates } : prev));

  async function handleSave() {
    if (!contractor || !form) return;
    if (!form.companyName.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const updated = await ContractorService.updateContractor(contractor.id, {
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        tradeType: form.tradeType.trim(),
        licenseNumber: form.licenseNumber.trim(),
        notes: form.notes.trim(),
        active: form.active,
      });
      if (!updated) {
        toast.error("Failed to save installer profile");
        return;
      }
      toast.success(`${updated.companyName} saved`);
      onSaved(updated);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Installer Profile</DialogTitle>
          <DialogDescription>
            Update contact and business details for this installer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ie-company">Company name</Label>
            <Input id="ie-company" value={form.companyName} onChange={(e) => patch({ companyName: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ie-contact">Contact name</Label>
            <Input id="ie-contact" value={form.contactName} onChange={(e) => patch({ contactName: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ie-email">Email</Label>
              <Input id="ie-email" type="email" value={form.email} onChange={(e) => patch({ email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ie-phone">Phone</Label>
              <Input id="ie-phone" value={form.phone} onChange={(e) => patch({ phone: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ie-trade">Trade</Label>
              <Input id="ie-trade" value={form.tradeType} onChange={(e) => patch({ tradeType: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ie-license">License #</Label>
              <Input id="ie-license" value={form.licenseNumber} onChange={(e) => patch({ licenseNumber: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ie-status">Status</Label>
            <Select value={form.active ? "active" : "inactive"} onValueChange={(v) => patch({ active: v === "active" })}>
              <SelectTrigger id="ie-status" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ie-notes">Notes</Label>
            <Textarea id="ie-notes" value={form.notes} onChange={(e) => patch({ notes: e.target.value })} rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
